import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import Input from "components/form/Input";
import { CustomSelect } from "ui/Input/Select";
import styles from "../Calendar.module.css";
import API from "constants/api";

const CATEGORY_API = `${API.BASE_URL}/guide/boats/category_to_boats?lang=en-GB`;
const EQUIPMENT_API = `${API.BASE_URL}/guide/boats/listing?lang=en-GB`;

const getCount = (val) => {
  if (Array.isArray(val)) return val.length
  return val ? 1 : 0
}

const normalizeMultiValue = (val) => {
  if (Array.isArray(val)) return val
  return val ? [val] : []
}

const filterEquipment = (equipment, category, search) =>
  equipment.filter(
    (eq) =>
      (!category?.length || category.includes(eq.category)) &&
      (!search || eq.label.toLowerCase().includes(search.toLowerCase()))
  )

const EquipmentFilters = ({ value = {}, onChange, onOptionsChange, hideSearch = false, fullWidth = false }) => {
  const [categories, setCategories] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const fetchOptions = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    Promise.all([
      fetch(CATEGORY_API, fetchOptions).then((r) => r.json()),
      fetch(EQUIPMENT_API, fetchOptions).then((r) => r.json()),
    ])
      .then(([catRes, eqRes]) => {
        const catOptions = (catRes.data || []).map((cat) => ({ label: cat.name, value: cat.id }));
        const eqOptions = (eqRes.data || []).map((eq) => ({ label: eq.equipment_title, value: eq.id, category: eq.category?.id }));
        setCategories(catOptions);
        setEquipment(eqOptions);
        if (onOptionsChange) {
          onOptionsChange({ equipmentOptions: eqOptions, categoryOptions: catOptions });
        }
      })
      .catch(() => {});
  }, []);

  // Filter equipment by search and selected category
  const filteredEquipment = filterEquipment(equipment, value.category, search);

  // Overlay logic for Figma-style count display
  const equipmentSelectRef = useRef(null);
  const categorySelectRef = useRef(null);
  const equipmentCount = getCount(value.equipment)
  const categoryCount = getCount(value.category)
  const normalizedEquipment = normalizeMultiValue(value.equipment)
  const normalizedCategory = normalizeMultiValue(value.category)

  return (
    <div className={styles.calendarFilters} style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: fullWidth ? "wrap" : "nowrap", width: fullWidth ? '100%' : 'auto' }}>
      {!hideSearch && (
        <Input
          placeholder="Search for Crew, Equipment"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={styles.calendarSearch}
          style={{ width: fullWidth ? '100%' : 290, borderRadius: 999 }}
        />
      )}
      <div style={{ position: 'relative', width: fullWidth ? '100%' : 180 }}>
        <CustomSelect
          ref={equipmentSelectRef}
          mode="multiple"
          placeholder="Equipment"
          value={normalizedEquipment}
          onChange={(val) => onChange({ ...value, equipment: val })}
          options={filteredEquipment}
          style={{ width: fullWidth ? '100%' : 180 }}
          allowClear
          maxTagCount={0}
          showSearch={false}
          tagRender={() => null}
        />
        {equipmentCount > 0 && (
          <div style={{
            position: 'absolute',
            left: 12,
            top: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'none',
            color: '#525455',
            fontWeight: 400,
            fontSize: 16,
            background: 'transparent',
          }}>
            {`Equipment (${equipmentCount})`}
          </div>
        )}
      </div>
      <div style={{ position: 'relative', width: fullWidth ? '100%' : 180 }}>
        <CustomSelect
          ref={categorySelectRef}
          mode="multiple"
          placeholder="Categories"
          value={normalizedCategory}
          onChange={(val) => onChange({ ...value, category: val })}
          options={categories}
          style={{ width: fullWidth ? '100%' : 180 }}
          allowClear
          maxTagCount={0}
          showSearch={false}
          tagRender={() => null}
        />
        {categoryCount > 0 && (
          <div style={{
            position: 'absolute',
            left: 12,
            top: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'none',
            color: '#525455',
            fontWeight: 400,
            fontSize: 16,
            background: 'transparent',
          }}>
            {`Categories (${categoryCount})`}
          </div>
        )}
      </div>
    </div>
  );
};

EquipmentFilters.propTypes = {
  value: PropTypes.shape({
    equipment: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
    category: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number])
  }),
  onChange: PropTypes.func,
  onOptionsChange: PropTypes.func,
  hideSearch: PropTypes.bool,
  fullWidth: PropTypes.bool
}

export default EquipmentFilters;
